/**
 * PropertyFileEditModal — bearbeitet die Metadaten eines bereits hochgeladenen Files.
 *
 * Use-Case: jemand laedt eine Rechnung hoch ohne Betrag oder Counterparty einzutragen.
 * Spaeter sollen die Felder nachgepflegt werden ohne neuen Upload. Der
 * `updateMetadata`-Endpoint nimmt IssuedOn etc. an — Path-Builder verschiebt das File
 * ggf. in einen anderen Folder (z.B. wenn IssuedOn nachgetragen wird). Der Kind wird
 * hier bewusst NICHT geaendert — das ist die explizite Reclassify-Aktion.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@/shared/components';
import {
  toApiError,
  useApi,
  type PropertyFileResponse,
  type UpdatePropertyFileMetadataRequest,
} from '@/shared/api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  file: PropertyFileResponse | null;
}

export function PropertyFileEditModal({ open, onOpenChange, propertyId, file }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const [counterparty, setCounterparty] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [issuedOn, setIssuedOn] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open && file) {
      setCounterparty(file.counterparty ?? '');
      setAmount(file.amount != null ? String(file.amount) : '');
      setCurrency(file.currency ?? 'EUR');
      setIssuedOn(file.issuedOn ?? '');
      setValidFrom(file.validFrom ?? '');
      setValidTo(file.validTo ?? '');
      setDescription(file.description ?? '');
    }
  }, [open, file]);

  const update = useMutation({
    mutationFn: (body: UpdatePropertyFileMetadataRequest) => {
      if (!file) return Promise.reject(new Error('Kein File ausgewählt'));
      return api.propertyFiles.updateMetadata(propertyId, file.id, body);
    },
    onSuccess: () => {
      toast.success('Metadaten aktualisiert');
      void qc.invalidateQueries({ queryKey: ['propertyFiles'] });
      onOpenChange(false);
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error(e.detail ?? e.title);
    },
  });

  function submit() {
    if (!file) return;
    update.mutate({
      counterparty: emptyToNull(counterparty),
      amount: amount.trim() ? Number(amount.replace(',', '.')) : null,
      currency: amount.trim() ? currency : null,
      issuedOn: emptyToNull(issuedOn),
      validFrom: emptyToNull(validFrom),
      validTo: emptyToNull(validTo),
      description: emptyToNull(description),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Metadaten bearbeiten</DialogTitle>
          {file && <p className="text-ink-faint mt-1 truncate text-xs">{file.fileName}</p>}
        </DialogHeader>
        <DialogBody className="space-y-3">
          <FormField label="Gegenpartei / Lieferant">
            <Input
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              placeholder="z.B. Vattenfall"
            />
          </FormField>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Betrag" className="col-span-2">
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
              />
            </FormField>
            <FormField label="Währung">
              <Input
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                maxLength={3}
              />
            </FormField>
          </div>
          <FormField label="Beleg-Datum" hint="Verschiebt die Datei ggf. in Jahr/Monat-Ordner.">
            <Input type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          </FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Gültig ab">
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </FormField>
            <FormField label="Gültig bis">
              <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Beschreibung">
            <Textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormField>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            Abbrechen
          </Button>
          <Button variant="brand" onClick={submit} loading={update.isPending} disabled={!file}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function emptyToNull(s: string): string | null {
  return s.trim() ? s.trim() : null;
}
